import { DocumentDefinition, RootElementOption } from '../../../models/datamapper/document';
import { NS_XML_SCHEMA_INSTANCE } from '../../../models/datamapper/standard-namespaces';
import { Types } from '../../../models/datamapper/types';
import { capitalize } from '../../../serializers/xml/utils/xml-utils';
import {
  XmlSchema,
  XmlSchemaAll,
  XmlSchemaAllMember,
  XmlSchemaAny,
  XmlSchemaAttribute,
  XmlSchemaAttributeGroup,
  XmlSchemaAttributeGroupMember,
  XmlSchemaAttributeGroupRef,
  XmlSchemaAttributeOrGroupRef,
  XmlSchemaChoice,
  XmlSchemaCollection,
  XmlSchemaComplexContentExtension,
  XmlSchemaComplexContentRestriction,
  XmlSchemaComplexType,
  XmlSchemaContentModel,
  XmlSchemaElement,
  XmlSchemaGroup,
  XmlSchemaGroupParticle,
  XmlSchemaGroupRef,
  XmlSchemaParticle,
  XmlSchemaRef,
  XmlSchemaSequence,
  XmlSchemaSequenceMember,
  XmlSchemaSimpleContentExtension,
  XmlSchemaSimpleContentRestriction,
  XmlSchemaSimpleType,
  XmlSchemaType,
  XmlSchemaUse,
} from '../../../xml-schema-ts';
import { XmlSchemaDocumentation } from '../../../xml-schema-ts/annotation/XmlSchemaDocumentation';
import { QName } from '../../../xml-schema-ts/QName';
import { XmlSchemaSimpleTypeContent } from '../../../xml-schema-ts/simple/XmlSchemaSimpleTypeContent';
import { XmlSchemaSimpleTypeList } from '../../../xml-schema-ts/simple/XmlSchemaSimpleTypeList';
import { XmlSchemaSimpleTypeRestriction } from '../../../xml-schema-ts/simple/XmlSchemaSimpleTypeRestriction';
import { XmlSchemaSimpleTypeUnion } from '../../../xml-schema-ts/simple/XmlSchemaSimpleTypeUnion';
import { XmlSchemaAnnotated } from '../../../xml-schema-ts/XmlSchemaAnnotated';
import { parseQNameString } from '../../namespace-util';
import { DocumentUtilService } from '../document-util.service';
import { XmlSchemaAnalysisService } from './xml-schema-analysis.service';
import type {
  CreateXmlSchemaDocumentResult,
  XmlSchemaParentType,
  XmlSchemaTypeFragment,
} from './xml-schema-document.model';
import { XmlSchemaDocument, XmlSchemaField } from './xml-schema-document.model';
import { XmlSchemaDocumentUtilService } from './xml-schema-document-util.service';
import { XmlSchemaTypesService } from './xml-schema-types.service';

const REGEX_BASE_URI = /baseUri="([^"]+)"/;

/**
 * The collection of XML schema handling logic. {@link createXmlSchemaDocument} consumes XML schema
 * file and generate a {@link XmlSchemaDocument} object.
 *
 * @see XmlSchemaDocumentUtilService
 */
export class XmlSchemaDocumentService {
  /**
   * Creates an XML Schema Document from a DocumentDefinition.
   * @param definition The DocumentDefinition containing schema information and configuration
   * @returns {@link CreateXmlSchemaDocumentResult} with document, root element options, and validation status
   */
  static createXmlSchemaDocument(
    definition: DocumentDefinition,
    namespaceMap: Record<string, string> = {},
  ): CreateXmlSchemaDocumentResult {
    const collection = new XmlSchemaCollection();
    const definitionFiles = definition.definitionFiles || {};
    collection.getSchemaResolver().addFiles(definitionFiles);

    const analysis = XmlSchemaAnalysisService.analyze(definitionFiles);
    if (analysis.errors.length > 0) {
      return XmlSchemaDocumentService.createErrorResult(analysis.errors, analysis.warnings, definition);
    }

    const loadResult = XmlSchemaDocumentService.loadSchemaFiles(collection, analysis, definitionFiles);
    if (loadResult.error) {
      return XmlSchemaDocumentService.createErrorResult(
        [{ message: loadResult.error.message, filePath: loadResult.error.filePath }],
        analysis.warnings,
        definition,
      );
    }

    const validationResult = XmlSchemaDocumentService.validateCollection(collection, definition);
    if (validationResult) {
      return validationResult;
    }

    const rootElement = XmlSchemaDocumentService.getRootElement(collection, definition);
    if ('validationStatus' in rootElement) {
      return rootElement;
    }

    const document = new XmlSchemaDocument(definition, collection, rootElement);

    XmlSchemaDocumentService.populateNamedTypeFragments(document);
    XmlSchemaDocumentService.populateElement(document, document.fields, document.rootElement!);

    DocumentUtilService.processOverrides(
      document,
      definition.fieldTypeOverrides ?? [],
      definition.choiceSelections ?? [],
      definition.fieldSubstitutions ?? [],
      namespaceMap,
      XmlSchemaTypesService.parseTypeOverride,
      XmlSchemaTypesService.resolveSubstitution,
    );

    const rootElementOptions = XmlSchemaDocumentUtilService.collectRootElementOptions(collection);
    const validationStatus = analysis.warnings.length > 0 ? 'warning' : 'success';

    return {
      validationStatus,
      warnings: analysis.warnings.length > 0 ? analysis.warnings : undefined,
      documentDefinition: definition,
      document,
      rootElementOptions,
    } as CreateXmlSchemaDocumentResult;
  }

  /**
   * Helper method to create an error result.
   */
  private static createErrorResult(
    errors: Array<{ message: string; filePath?: string }>,
    warnings: Array<{ message: string; filePath?: string }>,
    definition: DocumentDefinition,
  ): CreateXmlSchemaDocumentResult {
    return {
      validationStatus: 'error',
      errors,
      warnings,
      documentDefinition: definition,
    };
  }

  /**
   * Helper method to load schema files into the collection.
   */
  private static loadSchemaFiles(
    collection: XmlSchemaCollection,
    analysis: { loadOrder: string[]; edges: Array<{ directive: { type: string }; to: string; from: string }> },
    definitionFiles: Record<string, string>,
  ): { error?: { message: string; filePath?: string } } {
    const includeTargets = XmlSchemaDocumentService.computeIncludeTargetsToSkip(analysis.loadOrder, analysis.edges);

    try {
      for (const path of analysis.loadOrder) {
        if (includeTargets.has(path)) continue;
        collection.read(definitionFiles[path], () => {}, path);
      }
      return {};
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const baseUriMatch = REGEX_BASE_URI.exec(errorMessage);
      const filePath = baseUriMatch?.[1];
      return { error: { message: errorMessage, filePath } };
    }
  }

  /**
   * Determines which include-target files can safely be skipped during direct loading.
   * Normally, files that are xs:include targets are loaded indirectly when their parent
   * schema processes the include directive. However, with circular includes (A includes B,
   * B includes A), all files in the cycle become include targets — skipping them all means
   * nothing loads. This method ensures at least one file per isolated cycle is promoted to
   * direct loading so the SchemaBuilder can process the cycle via its built-in
   * `collection.check(key)` guard.
   */
  private static computeIncludeTargetsToSkip(
    loadOrder: string[],
    edges: Array<{ directive: { type: string }; to: string; from: string }>,
  ): Set<string> {
    const includeEdges = edges.filter((e) => e.directive.type === 'include');
    const includeTargets = new Set(includeEdges.map((e) => e.to));
    const adjacencyMap = XmlSchemaDocumentService.buildIncludeAdjacency(includeEdges);

    const reachable = new Set<string>();
    const seeds = loadOrder.filter((p) => !includeTargets.has(p));
    XmlSchemaDocumentService.bfsReachable(seeds, adjacencyMap, reachable);

    for (const path of loadOrder) {
      if (reachable.has(path)) continue;
      includeTargets.delete(path);
      XmlSchemaDocumentService.bfsReachable([path], adjacencyMap, reachable);
    }

    return includeTargets;
  }

  private static buildIncludeAdjacency(includeEdges: Array<{ from: string; to: string }>): Map<string, string[]> {
    const adjacencyMap = new Map<string, string[]>();
    for (const edge of includeEdges) {
      const list = adjacencyMap.get(edge.from) ?? [];
      list.push(edge.to);
      adjacencyMap.set(edge.from, list);
    }
    return adjacencyMap;
  }

  private static bfsReachable(seeds: string[], adj: Map<string, string[]>, reachable: Set<string>): void {
    const queue = [...seeds];
    for (const seed of seeds) {
      reachable.add(seed);
    }
    while (queue.length > 0) {
      const file = queue.shift()!;
      for (const target of adj.get(file) ?? []) {
        if (!reachable.has(target)) {
          reachable.add(target);
          queue.push(target);
        }
      }
    }
  }

  /**
   * Helper method to validate the schema collection.
   */
  private static validateCollection(
    collection: XmlSchemaCollection,
    definition: DocumentDefinition,
  ): CreateXmlSchemaDocumentResult | null {
    if (collection.getXmlSchemas().length === 0) {
      return {
        validationStatus: 'error',
        errors: [{ message: 'No schema files provided in DocumentDefinition' }],
        documentDefinition: definition,
      };
    }

    const totalElements = XmlSchemaDocumentUtilService.getElementCount(collection);
    if (totalElements === 0) {
      return {
        validationStatus: 'error',
        errors: [{ message: "There's no top level Element in the schema" }],
        documentDefinition: definition,
      };
    }

    return null;
  }

  /**
   * Helper method to get the root element.
   */
  private static getRootElement(
    collection: XmlSchemaCollection,
    definition: DocumentDefinition,
  ): XmlSchemaElement | CreateXmlSchemaDocumentResult {
    try {
      return XmlSchemaDocumentUtilService.determineRootElement(collection, definition.rootElementChoice);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return {
        validationStatus: 'error',
        errors: [{ message: errorMessage }],
        documentDefinition: definition,
      };
    }
  }

  /**
   * Adds additional schema files to an existing document's schema collection.
   * This is useful when field type overrides reference types defined in additional schema files.
   * @param document - The document whose schema collection will be updated
   * @param additionalFiles - Map of file paths to file contents to add
   */
  static addSchemaFiles(document: XmlSchemaDocument, additionalFiles: Record<string, string>): void {
    const collection = document.xmlSchemaCollection;
    collection.getSchemaResolver().addFiles(additionalFiles);
    XmlSchemaDocumentUtilService.loadXmlSchemaFiles(collection, additionalFiles);
    XmlSchemaDocumentService.populateNamedTypeFragments(document);
    XmlSchemaDocumentService.rebuildAbstractWrapperChildren(document);
  }

  /**
   * Rebuilds the children of abstract wrapper fields after schema attachment.
   * Abstract wrapper candidates are open-ended — new candidates can appear in
   * separate schema files. This re-queries {@link XmlSchemaTypesService.getSubstitutionGroupMembers}
   * and updates `.fields` while reusing existing `IField` objects where possible
   * to preserve FieldItem references in the visualization layer.
   * Choice wrapper children are not rebuilt because their members are inline and static.
   */
  private static rebuildAbstractWrapperChildren(document: XmlSchemaDocument): void {
    for (const field of document.fields) {
      XmlSchemaDocumentService.rebuildAbstractWrapperField(field as XmlSchemaField, document.xmlSchemaCollection);
    }
    for (const fragment of Object.values(document.namedTypeFragments)) {
      for (const field of fragment.fields) {
        XmlSchemaDocumentService.rebuildAbstractWrapperField(field as XmlSchemaField, document.xmlSchemaCollection);
      }
    }
  }

  private static rebuildAbstractWrapperField(field: XmlSchemaField, collection: XmlSchemaCollection): void {
    if (field.wrapperKind === 'abstract') {
      const headQName = new QName(field.namespaceURI, field.name);
      const headElement = collection.getElementByQName(headQName);
      if (headElement) {
        const members = XmlSchemaTypesService.getSubstitutionGroupMembers(headElement, collection);
        const existingByKey = new Map(field.fields.map((f) => [`${f.namespaceURI}|${f.name}`, f]));
        const newFields: XmlSchemaField[] = [];
        for (const member of members) {
          const memberName = member.getWireName()!.getLocalPart()!;
          const memberNs = member.getWireName()!.getNamespaceURI();
          const key = `${memberNs}|${memberName}`;
          const existing = existingByKey.get(key);
          if (existing) {
            newFields.push(existing as XmlSchemaField);
          } else {
            XmlSchemaDocumentService.populateElement(field, newFields, member);
          }
        }
        field.fields = newFields;
      }
    }
    for (const child of field.fields) {
      XmlSchemaDocumentService.rebuildAbstractWrapperField(child as XmlSchemaField, collection);
    }
  }

  /**
   * Removes a schema file from the definition and re-creates the document with updated analysis.
   * The original definition is not mutated. The returned result always contains the updated
   * {@link DocumentDefinition} even when validation fails, so callers can continue working with it
   * (e.g. adding replacement files).
   *
   * @param definition - The current document definition containing schema files
   * @param filePath - The key of the schema file to remove from {@link DocumentDefinition.definitionFiles}
   * @param namespaceMap namespace prefix-URI map
   * @returns A {@link CreateXmlSchemaDocumentResult} with updated validation status, errors/warnings, and definition
   */
  static removeSchemaFile(
    definition: DocumentDefinition,
    filePath: string,
    namespaceMap: Record<string, string>,
  ): CreateXmlSchemaDocumentResult {
    const updatedFiles = { ...definition.definitionFiles };
    delete updatedFiles[filePath];

    const updatedDefinition = new DocumentDefinition(
      definition.documentType,
      definition.definitionType,
      definition.name,
      updatedFiles,
      definition.rootElementChoice,
      definition.fieldTypeOverrides,
      definition.choiceSelections,
      definition.fieldSubstitutions,
    );

    // Try to create the Document object. It could fail if the root element user chose was defined in the removed
    // schema file. In that case, we unset `updatedDefinition.rootElementChoice` and retry.
    const result = XmlSchemaDocumentService.createXmlSchemaDocument(updatedDefinition, namespaceMap);

    if (result.document) {
      // When no explicit rootElementChoice, check if the effective root changed; if so, stale paths must be cleared.
      if (
        !definition.rootElementChoice &&
        XmlSchemaDocumentService.hasRootElementChanged(result.document, definition, namespaceMap)
      ) {
        updatedDefinition.fieldTypeOverrides = [];
        updatedDefinition.choiceSelections = [];
        updatedDefinition.fieldSubstitutions = [];
        return XmlSchemaDocumentService.createXmlSchemaDocument(updatedDefinition, namespaceMap);
      }
      return result;
    }

    if (!definition.rootElementChoice) {
      return result;
    }

    // Unset the root element and retry
    updatedDefinition.rootElementChoice = undefined;
    updatedDefinition.fieldTypeOverrides = [];
    updatedDefinition.choiceSelections = [];
    updatedDefinition.fieldSubstitutions = [];
    return XmlSchemaDocumentService.createXmlSchemaDocument(updatedDefinition, namespaceMap);
  }

  /**
   * Returns true when the effective root element of `document` differs from the root implied by the
   * first schema path stored in `definition`'s path-based metadata. Used by {@link removeSchemaFile}
   * to detect a silent root change when no explicit rootElementChoice is set.
   */
  private static hasRootElementChanged(
    document: XmlSchemaDocument,
    definition: DocumentDefinition,
    namespaceMap: Record<string, string>,
  ): boolean {
    const newRootQName = document.rootElement?.getQName();
    const rootSubstitutionPath = (definition.fieldSubstitutions ?? [])
      .map((s) => s.schemaPath)
      .find((path) => path.split('/').filter(Boolean).length === 1);
    const firstPath =
      rootSubstitutionPath ??
      (definition.fieldTypeOverrides ?? [])
        .map((o) => o.schemaPath)
        .concat((definition.choiceSelections ?? []).map((o) => o.schemaPath))
        .concat((definition.fieldSubstitutions ?? []).map((o) => o.schemaPath))[0];
    if (!newRootQName || !firstPath) return false;
    // Schema paths are formatted as "/<prefix>:<localPart>/..." (prefix may be absent for no-namespace schemas).
    // Extract the first non-empty segment, split on ':', resolve the prefix to a namespace URI via namespaceMap,
    // and compare namespace URI + localPart against the new document's root element QName.
    const rootSegment = firstPath.split('/').find((s) => s.length > 0) ?? '';
    const { prefix, localPart } = parseQNameString(rootSegment);
    if (prefix && !(prefix in namespaceMap)) {
      return true;
    }
    const nsURI = prefix ? namespaceMap[prefix] : '';
    return nsURI !== (newRootQName.getNamespaceURI() ?? '') || localPart !== newRootQName.getLocalPart();
  }

  /**
   * Recreates an {@link XmlSchemaDocument} with a new root element, reusing the existing {@link XmlSchemaCollection}.
   * @param document - The existing document whose schema collection will be reused
   * @param rootElementOption - Specifies the namespace URI and local name of the new root element
   * @returns A new {@link XmlSchemaDocument} with the updated root element and fully populated fields
   * @throws Error if the specified root element is not found in the schema collection
   */
  static updateRootElement(document: XmlSchemaDocument, rootElementOption: RootElementOption): XmlSchemaDocument {
    const newRootQName = new QName(rootElementOption.namespaceUri, rootElementOption.name);
    const newRootElement = document.xmlSchemaCollection.getElementByQName(newRootQName);

    if (!newRootElement) {
      throw new Error(`Unable to find a root element ${newRootQName.toString()}`);
    }

    document.definition.fieldTypeOverrides = [];
    document.definition.choiceSelections = [];
    document.definition.fieldSubstitutions = [];

    const newDocument = new XmlSchemaDocument(document.definition, document.xmlSchemaCollection, newRootElement);

    XmlSchemaDocumentService.populateNamedTypeFragments(newDocument);
    XmlSchemaDocumentService.populateElement(newDocument, newDocument.fields, newDocument.rootElement!);

    return newDocument;
  }

  private static populateSimpleNamedTypeFragment(
    document: XmlSchemaDocument,
    typeQName: QName,
    schemaType: XmlSchemaSimpleType,
  ) {
    const typeFragmentName = typeQName.toString();

    if (document.namedTypeFragments[typeFragmentName]) {
      return;
    }

    const fields: XmlSchemaField[] = [];
    const typeFragment: XmlSchemaTypeFragment = { fields, namedTypeFragmentRefs: [] };
    document.namedTypeFragments[typeFragmentName] = typeFragment;

    const simpleContent = schemaType.getContent();
    simpleContent && XmlSchemaDocumentService.populateSimpleTypeContent(document, typeFragment, simpleContent);
  }

  private static populateComplexNamedTypeFragment(
    document: XmlSchemaDocument,
    typeQName: QName,
    schemaType: XmlSchemaComplexType,
  ) {
    const typeFragmentName = typeQName.toString();

    if (document.namedTypeFragments[typeFragmentName]) {
      return;
    }

    const fields: XmlSchemaField[] = [];
    const typeFragment: XmlSchemaTypeFragment = { type: Types.Container, fields, namedTypeFragmentRefs: [] };
    document.namedTypeFragments[typeFragmentName] = typeFragment;

    XmlSchemaDocumentService.populateContentModel(document, typeFragment, schemaType.getContentModel());
    const attributes = schemaType.getAttributes();
    for (const attr of attributes) {
      XmlSchemaDocumentService.populateAttributeOrGroupRef(document, fields, attr);
    }
    XmlSchemaDocumentService.populateParticle(document, fields, schemaType.getParticle());
  }

  private static populateNamedTypeFragmentsForSchema(document: XmlSchemaDocument, schema: XmlSchema) {
    const schemaTypes = schema.getSchemaTypes();
    for (const [typeQName, schemaType] of schemaTypes.entries()) {
      if (schemaType instanceof XmlSchemaSimpleType) {
        XmlSchemaDocumentService.populateSimpleNamedTypeFragment(document, typeQName, schemaType);
      } else if (schemaType instanceof XmlSchemaComplexType) {
        XmlSchemaDocumentService.populateComplexNamedTypeFragment(document, typeQName, schemaType);
      }
    }
  }

  /**
   * Populates all named type definitions (complexType and simpleType) from the schema into namedTypeFragments.
   * Must be called before processing elements to ensure base types are available for extensions and restrictions.
   * @param document - The document whose schema collection will be traversed and whose namedTypeFragments will be populated
   */
  static populateNamedTypeFragments(document: XmlSchemaDocument) {
    const schemas = document.xmlSchemaCollection.getUserSchemas();
    for (const schema of schemas) {
      XmlSchemaDocumentService.populateNamedTypeFragmentsForSchema(document, schema);
    }
    for (const schema of schemas) {
      XmlSchemaDocumentService.populateGlobalElementFragmentsForSchema(document, schema);
    }
  }

  private static populateSimpleTypeContent(
    document: XmlSchemaDocument,
    typeFragment: XmlSchemaTypeFragment,
    content: XmlSchemaSimpleTypeContent,
  ) {
    if (content instanceof XmlSchemaSimpleTypeRestriction) {
      const baseTypeQName = content.getBaseTypeName();
      XmlSchemaDocumentService.populateSimpleContentRestrictionBaseType(document, typeFragment, baseTypeQName);
      // TODO collect&store restrictions to somewhere in the XmlSchemaField
    } else if (content instanceof XmlSchemaSimpleTypeList) {
      // TODO
    } else if (content instanceof XmlSchemaSimpleTypeUnion) {
      // TODO
    }
  }

  /**
   * Populates an XML element as an {@link XmlSchemaField} into the given fields array.
   * Skips concrete elements that already exist in fields (by name, namespace, and isAttribute). Resolves element
   * references and delegates to schema type handling to populate child fields and type metadata.
   *
   * Per the XSD spec, `abstract` is only valid on global element declarations — element
   * references (`<xs:element ref="..."/>`) cannot carry it. Therefore abstract detection
   * always checks `resolvedElement` (the ref target or the element itself), not the particle.
   * When abstract, delegates to {@link populateAbstractElement} which creates a wrapper
   * field with concrete substitution candidates as children.
   *
   * @param parent - The parent document or field that owns the fields array
   * @param fields - The target array to append the new field to
   * @param element - The XML schema element to convert into a field
   */
  static populateElement(parent: XmlSchemaParentType, fields: XmlSchemaField[], element: XmlSchemaElement) {
    const name = element.getWireName()!.getLocalPart()!;
    const refTarget = element.getRef().getTarget();
    const resolvedElement = refTarget ?? element;
    const namespaceURI = resolvedElement.getWireName()!.getNamespaceURI();
    const ownerDoc = ('ownerDocument' in parent ? parent.ownerDocument : parent) as XmlSchemaDocument;

    if (resolvedElement.isAbstract()) {
      XmlSchemaDocumentService.populateAbstractElement(parent, fields, element, resolvedElement);
      return;
    }

    const existing = fields.find((f) => f.name === name && !f.isAttribute && f.namespaceURI === namespaceURI);
    if (existing) {
      return;
    }

    const field: XmlSchemaField = new XmlSchemaField(parent, name, false);
    field.namespaceURI = namespaceURI;
    field.namespacePrefix = resolvedElement.getWireName()!.getPrefix();
    field.defaultValue = resolvedElement.defaultValue || resolvedElement.fixedValue;
    field.nillable = resolvedElement.isNillable();
    field.minOccurs = element.getMinOccurs();
    field.maxOccurs = element.getMaxOccurs();
    field.minOccursExplicit = element.isMinOccursExplicit();
    field.maxOccursExplicit = element.isMaxOccursExplicit();
    field.description = XmlSchemaDocumentService.extractDocumentationFromAnnotated(resolvedElement);
    fields.push(field);

    ownerDoc.totalFieldCount++;

    const schemaType = resolvedElement.getSchemaType();
    if (refTarget !== null && schemaType instanceof XmlSchemaComplexType && !schemaType.getQName()) {
      XmlSchemaDocumentService.populateGlobalElementFragment(ownerDoc, resolvedElement, schemaType, field);
    } else {
      XmlSchemaDocumentService.populateSchemaType(ownerDoc, field, schemaType);
    }

    if (field.nillable) {
      XmlSchemaDocumentService.populateNillableAttribute(field);
    }
  }

  private static ensureGlobalElementFragment(
    document: XmlSchemaDocument,
    element: XmlSchemaElement,
    schemaType: XmlSchemaComplexType,
  ): string {
    const wireName = element.getWireName()!;
    const fragmentKey = XmlSchemaDocumentUtilService.buildElementFragmentKey(
      wireName.getNamespaceURI(),
      wireName.getLocalPart()!,
    );

    if (!document.namedTypeFragments[fragmentKey]) {
      const fragmentFields: XmlSchemaField[] = [];
      const fragment: XmlSchemaTypeFragment = { fields: fragmentFields, namedTypeFragmentRefs: [] };
      document.namedTypeFragments[fragmentKey] = fragment;

      XmlSchemaDocumentService.populateContentModel(document, fragment, schemaType.getContentModel());
      const attributes = schemaType.getAttributes();
      for (const attr of attributes) {
        XmlSchemaDocumentService.populateAttributeOrGroupRef(document, fragmentFields, attr);
      }
      XmlSchemaDocumentService.populateParticle(document, fragmentFields, schemaType.getParticle());
    }

    return fragmentKey;
  }

  private static populateGlobalElementFragment(
    document: XmlSchemaDocument,
    element: XmlSchemaElement,
    schemaType: XmlSchemaComplexType,
    field: XmlSchemaField,
  ) {
    const fragmentKey = XmlSchemaDocumentService.ensureGlobalElementFragment(document, element, schemaType);
    field.type = Types.Container;
    field.namedTypeFragmentRefs.push(fragmentKey);
  }

  private static populateGlobalElementFragmentsForSchema(document: XmlSchemaDocument, schema: XmlSchema) {
    for (const element of schema.getElements().values()) {
      const schemaType = element.getSchemaType();
      if (schemaType instanceof XmlSchemaComplexType && !schemaType.getQName()) {
        const wireName = element.getWireName();
        if (wireName?.getLocalPart()) {
          XmlSchemaDocumentService.ensureGlobalElementFragment(document, element, schemaType);
        }
      }
    }
  }

  private static populateSchemaType(
    ownerDocument: XmlSchemaDocument,
    field: XmlSchemaField,
    schemaType: XmlSchemaType | null,
  ) {
    if (!schemaType) return;
    if (schemaType instanceof XmlSchemaSimpleType) {
      const newType = XmlSchemaDocumentUtilService.getFieldTypeFromName(schemaType.getName());
      if (!field.type || field.type === Types.AnyType) {
        field.type = newType;
      }
      const simpleTypeQName = schemaType.getQName();
      if (simpleTypeQName) {
        field.typeQName = simpleTypeQName;
      }
      return;
    } else if (!(schemaType instanceof XmlSchemaComplexType)) {
      throw new TypeError(`Unknown schema type class: ${typeof schemaType}`);
    }

    field.type = Types.Container;
    if (schemaType.isAbstract()) {
      field.isAbstractType = true;
    }
    const typeQName = schemaType.getQName();
    if (typeQName) {
      field.typeQName = typeQName;
      const namespace = typeQName.getNamespaceURI();
      if (!XmlSchemaDocumentUtilService.isStandardXmlNamespace(namespace)) {
        if (!field.namedTypeFragmentRefs.includes(typeQName.toString())) {
          field.namedTypeFragmentRefs.push(typeQName.toString());
        }
      }
      return;
    }

    XmlSchemaDocumentService.populateContentModel(ownerDocument, field, schemaType.getContentModel());
    const attributes: XmlSchemaAttributeOrGroupRef[] = schemaType.getAttributes();
    attributes.forEach((attr) => {
      XmlSchemaDocumentService.populateAttributeOrGroupRef(field, field.fields, attr);
    });
    XmlSchemaDocumentService.populateParticle(field, field.fields, schemaType.getParticle());
  }

  private static populateAttributeOrGroupRef(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    attr: XmlSchemaAttributeOrGroupRef,
  ) {
    if (attr instanceof XmlSchemaAttribute) {
      XmlSchemaDocumentService.populateAttribute(parent, fields, attr);
    } else if (attr instanceof XmlSchemaAttributeGroupRef) {
      XmlSchemaDocumentService.populateAttributeGroupRef(parent, fields, attr);
    }
  }

  /**
   * Extract documentation text from an XML Schema annotated object (element or attribute).
   * @param annotated - The annotated schema object (element, attribute, etc.)
   * @returns Documentation text, or undefined if no documentation found
   */
  private static extractDocumentationFromAnnotated(annotated: XmlSchemaAnnotated): string | undefined {
    const annotation = annotated.getAnnotation();
    if (!annotation) {
      return undefined;
    }

    const items = annotation.getItems();
    for (const item of items) {
      if (!(item instanceof XmlSchemaDocumentation)) continue;

      const markup = item.getMarkup();
      if (!markup || markup.length === 0) continue;

      let text = '';
      for (const node of markup) {
        if (node.textContent) {
          text += node.textContent;
        }
      }
      text = text.trim().replace(/\s+/g, ' ');
      if (text) {
        return text;
      }
    }

    return undefined;
  }

  /**
   * Adds a synthetic xsi:nil attribute child to a nillable XML element field.
   * This allows the Document tree to represent the nil state distinctly from an empty element.
   */
  private static populateNillableAttribute(parent: XmlSchemaField) {
    const alreadyExists = parent.fields.some(
      (field) => field.isAttribute && field.name === 'nil' && field.namespaceURI === NS_XML_SCHEMA_INSTANCE,
    );
    if (alreadyExists) {
      return;
    }

    const nillableField = new XmlSchemaField(parent, 'nil', true);
    nillableField.displayName = 'xsi:nil';
    nillableField.namespacePrefix = 'xsi';
    nillableField.namespaceURI = NS_XML_SCHEMA_INSTANCE;
    nillableField.type = Types.Boolean;
    nillableField.minOccurs = 0;
    nillableField.maxOccurs = 1;
    nillableField.minOccursExplicit = true;
    nillableField.maxOccursExplicit = true;
    parent.fields.push(nillableField);
    parent.ownerDocument.totalFieldCount++;
  }

  /**
   * Populates an XML attribute as an {@link XmlSchemaField} into the given fields array.
   * Skips attributes that already exist in fields (by name, namespace, and isAttribute). Handles required,
   * optional, and prohibited use cases by setting minOccurs and maxOccurs accordingly.
   * @param parent - The parent document or field that owns the fields array
   * @param fields - The target array to append the new field to
   * @param attr - The XML schema attribute to convert into a field
   */
  static populateAttribute(parent: XmlSchemaParentType, fields: XmlSchemaField[], attr: XmlSchemaAttribute) {
    const name = attr.getWireName()!.getLocalPart()!;
    const namespaceURI = attr.getWireName()!.getNamespaceURI();
    const ownerDoc = ('ownerDocument' in parent ? parent.ownerDocument : parent) as XmlSchemaDocument;

    const existing = fields.find((f) => f.name === name && f.isAttribute && f.namespaceURI === namespaceURI);
    if (existing) {
      return;
    }

    const field = new XmlSchemaField(parent, name, true);
    field.namespaceURI = namespaceURI;
    field.namespacePrefix = attr.getWireName()!.getPrefix();
    field.defaultValue = attr.getDefaultValue() || attr.getFixedValue();
    const attrTypeName = attr.getSchemaTypeName()?.getLocalPart();
    field.type = (attrTypeName ? Types[capitalize(attrTypeName) as keyof typeof Types] : null) || Types.AnyType;
    field.description = XmlSchemaDocumentService.extractDocumentationFromAnnotated(attr);
    fields.push(field);

    ownerDoc.totalFieldCount++;

    const attrSchemaTypeQName = attr.getSchemaTypeName();
    if (attrSchemaTypeQName) {
      field.typeQName = attrSchemaTypeQName;
    }
    const userDefinedAttrType =
      attrSchemaTypeQName &&
      XmlSchemaDocumentUtilService.lookupSchemaType(ownerDoc.xmlSchemaCollection, attrSchemaTypeQName);
    if (userDefinedAttrType) {
      field.namedTypeFragmentRefs.push(attrSchemaTypeQName.toString());
    }

    const use = attr.getUse();
    switch (use) {
      case XmlSchemaUse.PROHIBITED:
        field.maxOccurs = 0;
        field.minOccurs = 0;
        field.minOccursExplicit = true;
        field.maxOccursExplicit = true;
        break;
      case XmlSchemaUse.REQUIRED:
        field.minOccurs = 1;
        field.maxOccurs = 1;
        field.minOccursExplicit = true;
        field.maxOccursExplicit = true;
        break;
      default:
        field.minOccurs = 0;
        field.maxOccurs = 1;
        field.minOccursExplicit = true;
        field.maxOccursExplicit = true;
        break;
    }
  }

  private static populateAttributeGroupRef(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    groupRef: XmlSchemaAttributeGroupRef,
    visitedGroups: Set<XmlSchemaAttributeGroup> = new Set(),
  ) {
    const ref: XmlSchemaRef<XmlSchemaAttributeGroup> = groupRef.getRef();
    XmlSchemaDocumentService.populateAttributeGroup(parent, fields, ref.getTarget(), visitedGroups);
  }

  private static populateAttributeGroupMember(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    member: XmlSchemaAttributeGroupMember,
    visitedGroups: Set<XmlSchemaAttributeGroup> = new Set(),
  ) {
    if (member instanceof XmlSchemaAttribute) {
      XmlSchemaDocumentService.populateAttribute(parent, fields, member);
    } else if (member instanceof XmlSchemaAttributeGroup) {
      XmlSchemaDocumentService.populateAttributeGroup(parent, fields, member, visitedGroups);
    } else if (member instanceof XmlSchemaAttributeGroupRef) {
      XmlSchemaDocumentService.populateAttributeGroupRef(parent, fields, member, visitedGroups);
    }
  }

  private static populateAttributeGroup(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    group: XmlSchemaAttributeGroup | null,
    visitedGroups: Set<XmlSchemaAttributeGroup> = new Set(),
  ) {
    if (group == null || visitedGroups.has(group)) {
      return;
    }
    visitedGroups.add(group);
    for (const member of group.getAttributes()) {
      XmlSchemaDocumentService.populateAttributeGroupMember(parent, fields, member, visitedGroups);
    }
  }

  private static populateParticle(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    particle: XmlSchemaParticle | null,
    visitedGroupRefs?: Set<string>,
  ) {
    if (particle == null) {
      return;
    }
    if (particle instanceof XmlSchemaAny) {
      XmlSchemaDocumentService.populateAny(fields, particle);
    } else if (particle instanceof XmlSchemaElement) {
      XmlSchemaDocumentService.populateElement(parent, fields, particle);
    } else if (particle instanceof XmlSchemaGroupParticle) {
      XmlSchemaDocumentService.populateGroupParticle(parent, fields, particle, visitedGroupRefs);
    } else if (particle instanceof XmlSchemaGroupRef) {
      XmlSchemaDocumentService.populateGroupRef(parent, fields, particle, visitedGroupRefs);
    }
  }

  private static populateAny(_fields: XmlSchemaField[], _schemaAny: XmlSchemaAny) {
    /* TODO - xs:any allows arbitrary elements */
  }

  private static populateGroupParticle(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    groupParticle: XmlSchemaGroupParticle | null,
    visitedGroupRefs?: Set<string>,
  ) {
    if (groupParticle == null) {
      return;
    }
    if (groupParticle instanceof XmlSchemaChoice) {
      XmlSchemaDocumentService.populateChoice(parent, fields, groupParticle, visitedGroupRefs);
    } else if (groupParticle instanceof XmlSchemaSequence) {
      for (const member of groupParticle.getItems()) {
        XmlSchemaDocumentService.populateSequenceMember(parent, fields, member, visitedGroupRefs);
      }
    } else if (groupParticle instanceof XmlSchemaAll) {
      for (const member of groupParticle.getItems()) {
        XmlSchemaDocumentService.populateAllMember(parent, fields, member, visitedGroupRefs);
      }
    }
  }

  /**
   * Creates a choice wrapper {@link XmlSchemaField} with {@link XmlSchemaField.wrapperKind} set to `'choice'`,
   * preserving minOccurs/maxOccurs from the xs:choice particle, and populates its children
   * from the choice's member items. The `{choice:N}` path index is derived at runtime by
   * {@link SchemaPathService.getChoiceSiblingIndex} from the field's position among sibling
   * `wrapperKind === 'choice'` fields — no explicit index is stored on the field.
   */
  private static populateChoice(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    choice: XmlSchemaChoice,
    visitedGroupRefs?: Set<string>,
  ) {
    const ownerDoc = ('ownerDocument' in parent ? parent.ownerDocument : parent) as XmlSchemaDocument;
    const choiceField = new XmlSchemaField(parent, '__choice__', false);
    choiceField.wrapperKind = 'choice';
    choiceField.minOccurs = choice.getMinOccurs();
    choiceField.maxOccurs = choice.getMaxOccurs();
    choiceField.minOccursExplicit = choice.isMinOccursExplicit();
    choiceField.maxOccursExplicit = choice.isMaxOccursExplicit();
    fields.push(choiceField);
    ownerDoc.totalFieldCount++;
    for (const member of choice.getItems()) {
      if (member instanceof XmlSchemaSequence) {
        XmlSchemaDocumentService.populateSequenceInChoice(choiceField, choiceField.fields, member, visitedGroupRefs);
      } else {
        XmlSchemaDocumentService.populateSequenceMember(choiceField, choiceField.fields, member, visitedGroupRefs);
      }
    }
    choiceField.displayName = 'choice';
  }

  private static populateSequenceInChoice(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    sequence: XmlSchemaSequence,
    visitedGroupRefs?: Set<string>,
  ) {
    const ownerDoc = ('ownerDocument' in parent ? parent.ownerDocument : parent) as XmlSchemaDocument;
    const seqField = new XmlSchemaField(parent, '__sequence__', false);
    seqField.wrapperKind = 'sequence';
    seqField.minOccurs = sequence.getMinOccurs();
    seqField.maxOccurs = sequence.getMaxOccurs();
    seqField.minOccursExplicit = sequence.isMinOccursExplicit();
    seqField.maxOccursExplicit = sequence.isMaxOccursExplicit();
    seqField.displayName = 'sequence';
    fields.push(seqField);
    ownerDoc.totalFieldCount++;
    for (const item of sequence.getItems()) {
      XmlSchemaDocumentService.populateSequenceMember(seqField, seqField.fields, item, visitedGroupRefs);
    }
  }

  /**
   * Creates an abstract element wrapper {@link XmlSchemaField} with {@link XmlSchemaField.wrapperKind}
   * set to `'abstract'`, and populates its children from the concrete substitution group members.
   *
   * Abstract wrappers use the actual element name when it is unique among siblings and add a stable
   * sibling-position suffix when it is not. The resolved element name remains the display name.
   * The `{abstract:N}` path index is derived at runtime by
   * {@link SchemaPathService.getAbstractSiblingIndex}, analogous to `{choice:N}`.
   *
   * @param parent - The parent document or field that owns the fields array
   * @param fields - The target array to append the new field to
   * @param element - The element particle providing minOccurs/maxOccurs (cardinality from context)
   * @param resolvedElement - The resolved (possibly ref-target) abstract element providing name and namespace
   */
  private static populateAbstractElement(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    element: XmlSchemaElement,
    resolvedElement: XmlSchemaElement,
  ) {
    const ownerDoc = ('ownerDocument' in parent ? parent.ownerDocument : parent) as XmlSchemaDocument;
    const displayName = resolvedElement.getWireName()!.getLocalPart()!;
    const namespaceURI = resolvedElement.getWireName()!.getNamespaceURI();
    let name = displayName;
    let siblingIndex = fields.length;
    while (fields.some((field) => field.name === name && !field.isAttribute && field.namespaceURI === namespaceURI)) {
      name = `${displayName}-${siblingIndex}`;
      siblingIndex++;
    }
    const abstractField = new XmlSchemaField(parent, name, false);
    abstractField.wrapperKind = 'abstract';
    abstractField.namespaceURI = namespaceURI;
    abstractField.namespacePrefix = resolvedElement.getWireName()!.getPrefix();
    abstractField.minOccurs = element.getMinOccurs();
    abstractField.maxOccurs = element.getMaxOccurs();
    abstractField.minOccursExplicit = element.isMinOccursExplicit();
    abstractField.maxOccursExplicit = element.isMaxOccursExplicit();
    abstractField.displayName = displayName;
    fields.push(abstractField);
    ownerDoc.totalFieldCount++;

    const members = XmlSchemaTypesService.getSubstitutionGroupMembers(resolvedElement, ownerDoc.xmlSchemaCollection);
    for (const member of members) {
      XmlSchemaDocumentService.populateElement(abstractField, abstractField.fields, member);
    }
  }

  private static populateGroupRef(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    groupRef: XmlSchemaGroupRef,
    visitedGroupRefs?: Set<string>,
  ) {
    const groupRefQName = groupRef.getRefName();
    if (!groupRefQName) return;

    const key = groupRefQName.toString();
    visitedGroupRefs ??= new Set();
    if (visitedGroupRefs.has(key)) return;
    visitedGroupRefs.add(key);

    const doc = ('ownerDocument' in parent ? parent.ownerDocument : parent) as XmlSchemaDocument;
    const group = doc.xmlSchemaCollection.getGroupByQName(groupRefQName);
    if (group) {
      XmlSchemaDocumentService.populateGroup(parent, fields, group, visitedGroupRefs);
    }

    visitedGroupRefs.delete(key);
  }

  private static populateSequenceMember(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    member: XmlSchemaSequenceMember,
    visitedGroupRefs?: Set<string>,
  ) {
    if (member instanceof XmlSchemaGroupRef) {
      XmlSchemaDocumentService.populateGroupRef(parent, fields, member, visitedGroupRefs);
    } else if (member instanceof XmlSchemaGroup) {
      XmlSchemaDocumentService.populateGroup(parent, fields, member, visitedGroupRefs);
    } else if (member instanceof XmlSchemaParticle) {
      XmlSchemaDocumentService.populateParticle(parent, fields, member, visitedGroupRefs);
    }
  }

  private static populateAllMember(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    member: XmlSchemaAllMember,
    visitedGroupRefs?: Set<string>,
  ) {
    if (member instanceof XmlSchemaParticle) {
      XmlSchemaDocumentService.populateParticle(parent, fields, member, visitedGroupRefs);
    }
  }

  private static populateGroup(
    parent: XmlSchemaParentType,
    fields: XmlSchemaField[],
    group: XmlSchemaGroup,
    visitedGroupRefs?: Set<string>,
  ) {
    XmlSchemaDocumentService.populateParticle(parent, fields, group.getParticle(), visitedGroupRefs);
  }

  private static populateContentModel(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    contentModel: XmlSchemaContentModel | null,
  ) {
    if (!contentModel) return;
    const content = contentModel.getContent();
    if (content instanceof XmlSchemaSimpleContentExtension) {
      XmlSchemaDocumentService.populateSimpleContentExtension(document, parent, content);
    } else if (content instanceof XmlSchemaSimpleContentRestriction) {
      XmlSchemaDocumentService.populateSimpleContentRestriction(document, parent, content);
    } else if (content instanceof XmlSchemaComplexContentExtension) {
      XmlSchemaDocumentService.populateComplexContentExtension(document, parent, content);
    } else if (content instanceof XmlSchemaComplexContentRestriction) {
      XmlSchemaDocumentService.populateComplexContentRestriction(document, parent, content);
    }
  }

  private static populateContentModelAttributes(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    contentModel: XmlSchemaContentModel | null,
  ) {
    if (!contentModel) return;
    const content = contentModel.getContent();
    if (content instanceof XmlSchemaSimpleContentExtension) {
      XmlSchemaDocumentService.populateSimpleContentExtension(document, parent, content);
    } else if (content instanceof XmlSchemaSimpleContentRestriction) {
      XmlSchemaDocumentService.populateSimpleContentRestriction(document, parent, content);
    } else if (content instanceof XmlSchemaComplexContentExtension) {
      XmlSchemaDocumentService.populateComplexContentExtensionAttributes(document, parent, content);
    } else if (content instanceof XmlSchemaComplexContentRestriction) {
      XmlSchemaDocumentService.populateComplexContentRestrictionAttributes(document, parent, content);
    }
  }

  private static populateContentModelElements(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    contentModel: XmlSchemaContentModel | null,
  ) {
    if (!contentModel) return;
    const content = contentModel.getContent();
    // SimpleContent doesn't have elements, only ComplexContent does
    if (content instanceof XmlSchemaComplexContentExtension) {
      XmlSchemaDocumentService.populateComplexContentExtensionElements(document, parent, content);
    } else if (content instanceof XmlSchemaComplexContentRestriction) {
      const parentForParticle = 'parent' in parent ? parent : document;
      XmlSchemaDocumentService.populateParticle(parentForParticle, parent.fields, content.getParticle());
    }
  }

  private static resolveBaseType(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    baseTypeQName: QName | null,
  ): XmlSchemaType | null {
    if (!baseTypeQName) return null;

    if (!parent.type) {
      parent.type = XmlSchemaDocumentUtilService.getFieldTypeFromName(baseTypeQName.getLocalPart());
    }

    return XmlSchemaDocumentUtilService.lookupSchemaType(document.xmlSchemaCollection, baseTypeQName);
  }

  private static populateSimpleExtensionBaseAttributes(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    userDefinedBaseType: XmlSchemaSimpleType,
  ) {
    const simpleBase = userDefinedBaseType;
    const simpleContent = simpleBase.getContent();
    if (simpleContent) {
      XmlSchemaDocumentService.populateSimpleTypeContent(document, parent, simpleContent);
    }
    if (!parent.type) {
      parent.type = XmlSchemaDocumentUtilService.getFieldTypeFromName(simpleBase.getName());
    }
  }

  private static populateComplexExtensionBaseAttributes(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    userDefinedBaseType: XmlSchemaComplexType,
  ) {
    const complexBase = userDefinedBaseType;
    const contentModel = complexBase.getContentModel();

    if (contentModel) {
      XmlSchemaDocumentService.populateContentModelAttributes(document, parent, contentModel);
    } else {
      const parentForAttribute = 'parent' in parent ? parent : document;
      for (const attr of complexBase.getAttributes()) {
        XmlSchemaDocumentService.populateAttributeOrGroupRef(parentForAttribute, parent.fields, attr);
      }
    }
  }

  private static populateExtensionBaseAttributes(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    baseTypeQName: QName | null,
  ) {
    const userDefinedBaseType = XmlSchemaDocumentService.resolveBaseType(document, parent, baseTypeQName);
    if (!userDefinedBaseType) return;

    if (userDefinedBaseType instanceof XmlSchemaSimpleType) {
      XmlSchemaDocumentService.populateSimpleExtensionBaseAttributes(document, parent, userDefinedBaseType);
    } else if (userDefinedBaseType instanceof XmlSchemaComplexType) {
      XmlSchemaDocumentService.populateComplexExtensionBaseAttributes(document, parent, userDefinedBaseType);
    }
  }

  private static populateExtensionBaseElements(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    baseTypeQName: QName | null,
  ) {
    const userDefinedBaseType = XmlSchemaDocumentService.resolveBaseType(document, parent, baseTypeQName);
    if (!userDefinedBaseType) return;

    if (userDefinedBaseType instanceof XmlSchemaComplexType) {
      const complexBase = userDefinedBaseType;
      const contentModel = complexBase.getContentModel();

      if (contentModel) {
        XmlSchemaDocumentService.populateContentModelElements(document, parent, contentModel);
      } else {
        const parentForParticle = 'parent' in parent ? parent : document;
        XmlSchemaDocumentService.populateParticle(parentForParticle, parent.fields, complexBase.getParticle());
      }
    }
  }

  private static populateSimpleContentRestrictionBaseType(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    baseTypeQName: QName | null,
  ) {
    const userDefinedBaseType = XmlSchemaDocumentService.resolveBaseType(document, parent, baseTypeQName);
    if (!userDefinedBaseType) return;

    if (userDefinedBaseType instanceof XmlSchemaComplexType) {
      const contentModel = userDefinedBaseType.getContentModel();

      if (contentModel) {
        const content = contentModel.getContent();
        if (
          content instanceof XmlSchemaSimpleContentExtension ||
          content instanceof XmlSchemaSimpleContentRestriction
        ) {
          XmlSchemaDocumentService.populateContentModelAttributes(document, parent, contentModel);
        }
      }
    } else if (userDefinedBaseType instanceof XmlSchemaSimpleType) {
      const simpleBase = userDefinedBaseType;
      const simpleContent = simpleBase.getContent();
      if (simpleContent) {
        XmlSchemaDocumentService.populateSimpleTypeContent(document, parent, simpleContent);
      }
      if (!parent.type) {
        parent.type = XmlSchemaDocumentUtilService.getFieldTypeFromName(simpleBase.getName());
      }
    }
  }

  /**
   * The simple {@code <xs:extension>} adds attributes in addition to what is defined in base. The base attributes
   * should come first.
   * @param document
   * @param parent
   * @param extension
   * @private
   */
  private static populateSimpleContentExtension(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    extension: XmlSchemaSimpleContentExtension,
  ) {
    XmlSchemaDocumentService.populateExtensionBaseAttributes(document, parent, extension.getBaseTypeName());
    const parentForAttribute = 'parent' in parent ? parent : document;
    for (const attr of extension.getAttributes()) {
      XmlSchemaDocumentService.populateAttributeOrGroupRef(parentForAttribute, parent.fields, attr);
    }
  }

  /**
   * The simple {@code <xs:restriction>} restricts the attributes defined in base.
   * @param document
   * @param parent
   * @param restriction
   * @private
   */
  private static populateSimpleContentRestriction(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    restriction: XmlSchemaSimpleContentRestriction,
  ) {
    const parentForAttribute = 'parent' in parent ? parent : document;
    for (const attr of restriction.getAttributes()) {
      XmlSchemaDocumentService.populateAttributeOrGroupRef(parentForAttribute, parent.fields, attr);
    }
    XmlSchemaDocumentService.populateSimpleContentRestrictionBaseType(document, parent, restriction.getBaseTypeName());
  }

  /**
   * The complex {@code <xs:extension>} adds elements and/or attributes. The base attributes and elements have
   * to come first. In order to show attributes before any elements in the DataMapper UI, attributes have to
   *  be added first, then elements. Therefore, the order is:
   *  <ol>
   *    <li>base attributes</li>
   *    <li>extension attributes</li>
   *    <li>base elements</li>
   *    <li>extension elements</li>
   *  </ol>
   * @param document
   * @param parent
   * @param extension
   * @private
   */
  private static populateComplexContentExtension(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    extension: XmlSchemaComplexContentExtension,
  ) {
    XmlSchemaDocumentService.populateComplexContentExtensionAttributes(document, parent, extension);
    XmlSchemaDocumentService.populateComplexContentExtensionElements(document, parent, extension);
  }

  private static populateComplexContentExtensionAttributes(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    extension: XmlSchemaComplexContentExtension,
  ) {
    const baseTypeQName = extension.getBaseTypeName();
    const baseType = baseTypeQName ? document.xmlSchemaCollection.getTypeByQName(baseTypeQName) : null;
    const baseHasContentModel = baseType instanceof XmlSchemaComplexType && baseType.getContentModel();

    if (baseHasContentModel) {
      XmlSchemaDocumentService.populateContentModelAttributes(document, parent, baseHasContentModel);
    } else {
      XmlSchemaDocumentService.populateExtensionBaseAttributes(document, parent, baseTypeQName);
    }
    const parentForAttribute = 'parent' in parent ? parent : document;
    for (const attr of extension.getAttributes()) {
      XmlSchemaDocumentService.populateAttributeOrGroupRef(parentForAttribute, parent.fields, attr);
    }
  }

  private static populateComplexContentExtensionElements(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    extension: XmlSchemaComplexContentExtension,
  ) {
    const baseTypeQName = extension.getBaseTypeName();
    const baseType = baseTypeQName ? document.xmlSchemaCollection.getTypeByQName(baseTypeQName) : null;
    const baseHasContentModel = baseType instanceof XmlSchemaComplexType && baseType.getContentModel();

    if (baseHasContentModel) {
      XmlSchemaDocumentService.populateContentModelElements(document, parent, baseHasContentModel);
    } else {
      XmlSchemaDocumentService.populateExtensionBaseElements(document, parent, baseTypeQName);
    }
    const parentForParticle = 'parent' in parent ? parent : document;
    XmlSchemaDocumentService.populateParticle(parentForParticle, parent.fields, extension.getParticle());
  }

  /**
   * The complex {@code <xs:restriction>} restricts the attributes and/or elements defined in base.
   * @param document
   * @param parent
   * @param restriction
   * @private
   */
  private static populateComplexContentRestriction(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    restriction: XmlSchemaComplexContentRestriction,
  ) {
    XmlSchemaDocumentService.populateComplexContentRestrictionAttributes(document, parent, restriction);
    const parentForParticle = 'parent' in parent ? parent : document;
    XmlSchemaDocumentService.populateParticle(parentForParticle, parent.fields, restriction.getParticle());
  }

  private static populateComplexContentRestrictionAttributes(
    document: XmlSchemaDocument,
    parent: XmlSchemaField | XmlSchemaTypeFragment,
    restriction: XmlSchemaComplexContentRestriction,
  ) {
    const parentForAttribute = 'parent' in parent ? parent : document;
    for (const attr of restriction.getAttributes()) {
      XmlSchemaDocumentService.populateAttributeOrGroupRef(parentForAttribute, parent.fields, attr);
    }

    const userDefinedBaseType = XmlSchemaDocumentService.resolveBaseType(
      document,
      parent,
      restriction.getBaseTypeName(),
    );
    if (!userDefinedBaseType) {
      return;
    }

    if (userDefinedBaseType instanceof XmlSchemaComplexType) {
      const complexBase = userDefinedBaseType;
      const contentModel = complexBase.getContentModel();

      if (contentModel) {
        XmlSchemaDocumentService.populateContentModelAttributes(document, parent, contentModel);
      } else {
        const parentForAttribute = 'parent' in parent ? parent : document;
        for (const attr of complexBase.getAttributes()) {
          XmlSchemaDocumentService.populateAttributeOrGroupRef(parentForAttribute, parent.fields, attr);
        }
      }
    }
  }
}
